import {isEmailValid} from '@cohort/shared/utils/validation';
import {classNames} from '@cohort/shared-frontend/utils/classNames';
import PrimaryButton from '@cohort/wallet/components/button/PrimaryButton';
import {Input} from '@cohort/wallet/components/forms/Input';
import {TextArea} from '@cohort/wallet/components/forms/TextArea';
import {useUserStore} from '@cohort/wallet/hooks/stores/user';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import {Transition} from '@headlessui/react';
import {CheckCircle} from '@phosphor-icons/react';
import React, {useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';

interface EmailTransferDetailsProps {
  recipientEmail: string;
  emailMessage: string;
  onEmailChange: (newEmail: string) => void;
  onEmailMessageChange: (newEmail: string) => void;
  onSubmitDetails: () => void;
}

export const EmailTransferDetails: React.FC<EmailTransferDetailsProps> = ({
  recipientEmail,
  emailMessage,
  onEmailChange,
  onEmailMessageChange,
  onSubmitDetails,
}) => {
  const [emailError, setEmailError] = useState('');

  const {t} = useTranslation('components', {keyPrefix: 'space.nftTransfer.emailTransferDetails'});
  const {accentColor, hasDarkBg} = useThemeContext();
  const user = useUserStore(store => store.user);

  const handleChangeEmail = (event: React.ChangeEvent<HTMLInputElement>): void => {
    onEmailChange(event.target.value);
  };

  const handleChangeEmailMessage = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
    onEmailMessageChange(event.target.value);
  };

  const onSubmitDetailsClick = useCallback(() => {
    if (user?.email === recipientEmail) {
      setEmailError(t('nftToYourselfError'));
    } else if (isEmailValid(recipientEmail)) {
      onSubmitDetails();
    } else {
      setEmailError(t('emailError'));
    }
  }, [user?.email, recipientEmail, onSubmitDetails, t]);

  const handleBlur = useCallback(() => {
    if (isEmailValid(recipientEmail)) {
      setEmailError('');
    } else {
      setEmailError(t('emailError'));
    }
  }, [recipientEmail, t]);

  return (
    <div>
      <h2 className={classNames('mb-1 text-sm', hasDarkBg ? 'text-white' : 'text-black/90')}>
        {t('emailInputTitle')}
      </h2>

      <div className="relative flex w-full">
        <Input
          testId="input-email-transfer-invitation"
          type="email"
          name="recipient email"
          accentColor={accentColor}
          placeholder="vitalik@getcohort.com"
          hasDarkBg={hasDarkBg}
          value={recipientEmail}
          onBlur={handleBlur}
          onChange={handleChangeEmail}
          required
        />

        <Transition
          show={isEmailValid(recipientEmail)}
          className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3"
          enter="transition duration-100 transform"
          enterFrom="opacity-0 translate-y-5"
          enterTo="opacity-100 "
          leave="transition duration-100 transform"
          leaveFrom="opacity-100"
          leaveTo="opacity-0 -translate-y-5"
        >
          <CheckCircle className="h-5 w-5 text-green-500" aria-hidden="true" />
        </Transition>
      </div>
      {emailError.length > 0 && (
        <p data-testid="error-email-transfer-invitation" className="mt-2 text-sm text-red-600">
          {emailError}
        </p>
      )}

      <h2 className={classNames('mb-1 mt-4 text-sm', hasDarkBg ? 'text-white' : 'text-black/90')}>
        {t('messageInputTitle')}
      </h2>
      <TextArea
        test-id="message-email-transfer-invitation"
        maxLength={500}
        name="email message"
        rows={5}
        cols={33}
        value={emailMessage}
        required
        placeholder={t('messageInputPlaceholder')}
        onChange={handleChangeEmailMessage}
      />
      <div className="mt-4">
        <PrimaryButton
          testId="continue-email-transfer-invitation"
          text={t('nextBtnText')}
          className="w-full md:w-fit"
          onClick={onSubmitDetailsClick}
          tracking={{
            namespace: 'digitalAssets.transfer.email.preview',
          }}
        />
      </div>
    </div>
  );
};
